import { useState, useEffect, useMemo, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import WTMonogram from '../components/WTMonogram';
import AuthLegalFooter from '../components/AuthLegalFooter';
import '../styles/login.css';
import { BACKEND_URL } from '../config';

import type { Session } from '../types/app';

interface RegisterProps {
  onLogin: (session: Session) => void;
}

interface OAuthProvider {
  id: string;
  name: string;
  authUrl: string;
}

const DEFAULT_OAUTH_PROVIDERS: OAuthProvider[] = [
  { id: 'google', name: 'Google', authUrl: `${BACKEND_URL}/api/auth/oauth/start/google` },
  { id: 'facebook', name: 'Facebook', authUrl: `${BACKEND_URL}/api/auth/oauth/start/facebook` },
];

function getPasswordStrength(pw: string): { level: number; label: string } {
  if (pw.length === 0) return { level: 0, label: '' };
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) score++;
  if (/\d/.test(pw)) score++;
  if (/[^a-zA-Z0-9]/.test(pw)) score++;

  if (score <= 1) return { level: 1, label: 'Faible' };
  if (score === 2) return { level: 2, label: 'Moyen' };
  if (score === 3) return { level: 3, label: 'Bon' };
  return { level: 4, label: 'Excellent' };
}

function isPasswordAcceptable(pw: string): boolean {
  return pw.length >= 8 && /[a-z]/.test(pw) && /[A-Z]/.test(pw) && /\d/.test(pw);
}

export default function Register({ onLogin }: RegisterProps) {
  const [showForm, setShowForm] = useState(false);
  const [pseudo, setPseudo] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [nom, setNom] = useState('');
  const [prenom, setPrenom] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [pseudoAvailable, setPseudoAvailable] = useState<boolean | null>(null);
  const [oauthProviders, setOauthProviders] = useState<OAuthProvider[]>(DEFAULT_OAUTH_PROVIDERS);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/auth/oauth/providers`)
      .then(res => res.json())
      .then(data => { if (data.providers?.length) setOauthProviders(data.providers); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (pseudo.length < 3) {
      setPseudoAvailable(null);
      return;
    }
    const timer = setTimeout(async () => {
      setChecking(true);
      try {
        const res = await fetch(`${BACKEND_URL}/api/auth/check-pseudo`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ pseudo }),
        });
        const data = await res.json();
        setPseudoAvailable(data.available ?? false);
      } catch {
        setPseudoAvailable(null);
      } finally {
        setChecking(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [pseudo]);

  const pwStrength = useMemo(() => getPasswordStrength(password), [password]);
  const pwAcceptable = useMemo(() => isPasswordAcceptable(password), [password]);
  const passwordsMatch = passwordConfirm.length === 0 || password === passwordConfirm;

  const emailTouched = email.length > 0;
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const canSubmit =
    pseudo.length >= 3 &&
    pseudoAvailable !== false &&
    emailValid &&
    pwAcceptable &&
    password === passwordConfirm &&
    nom.trim().length > 0 &&
    prenom.trim().length > 0 &&
    !loading;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pseudo: pseudo.trim(),
          password,
          email: email.trim().toLowerCase(),
          nom: nom.trim(),
          prenom: prenom.trim(),
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        const messages: Record<string, string> = {
          pseudo_taken: 'Ce pseudo est déjà utilisé.',
          pseudo_forbidden: 'Ce pseudo n\'est pas autorisé.',
          invalid_payload: data.details || 'Données invalides.',
        };
        throw new Error(messages[data.error] || data.message || 'Erreur lors de l\'inscription');
      }

      // Auto-login : le backend renvoie un JWT. On enchaîne directement sur
      // l'onboarding (choix d'avatar) puis l'app, sans seconde saisie pseudo/mdp.
      if (data.token) {
        onLogin({
          pseudo: data.user.pseudo,
          pseudoDisplay: data.user.pseudo_display || data.user.pseudo,
          compet: false,
          token: data.token,
          hashPass: data.hashPass || data.hash_pass,
          isNewAccount: true,
        });
      } else {
        // Fallback rétro-compat : si l'API renvoie pas de token, on
        // affiche un écran de succès statique.
        setError('Compte créé. Veuillez vous connecter manuellement.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de l\'inscription');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-bg" />

      <div className="login-card">
        <div className="login-header">
          <WTMonogram />
          <h1 className="login-title">Webtarot</h1>
          <p className="login-tagline">Créer votre compte</p>
        </div>

        <div className="login-body">
          {/* OAuth - hidden when pseudo form is open */}
          {!showForm && (
            <>
              <div className="oauth-buttons">
                {oauthProviders.map(provider => (
                  <a
                    key={provider.id}
                    href={provider.authUrl}
                    className={`oauth-btn oauth-btn-${provider.id}`}
                  >
                    <OAuthIcon provider={provider.id} />
                    <span>S'inscrire avec {provider.name}</span>
                  </a>
                ))}
              </div>

              <button
                type="button"
                className="oauth-btn oauth-btn-pseudo"
                onClick={() => setShowForm(true)}
              >
                <PseudoIcon />
                <span>S'inscrire avec un pseudo</span>
              </button>
            </>
          )}

          {/* Traditional registration form */}
          {showForm && (
            <div className="pseudo-form-section">
              <form onSubmit={handleSubmit} className="login-form">
                {error && <div className="login-error">{error}</div>}

                <div className="form-group">
                  <label htmlFor="reg-pseudo">Pseudo *</label>
                  <input
                    type="text"
                    id="reg-pseudo"
                    value={pseudo}
                    onChange={(e) => setPseudo(e.target.value)}
                    placeholder="Votre pseudo de jeu (min. 3 car.)"
                    minLength={3}
                    maxLength={25}
                    autoFocus
                    required
                  />
                  {pseudo.length >= 3 && (
                    <div className={`pseudo-check ${checking ? 'checking' : pseudoAvailable ? 'available' : 'taken'}`}>
                      {checking
                        ? 'Vérification...'
                        : pseudoAvailable
                          ? 'Pseudo disponible'
                          : 'Pseudo déjà utilisé'}
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="reg-email">Adresse email *</label>
                  <input
                    type="email"
                    id="reg-email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="votre@email.com"
                    required
                  />
                  {emailTouched && email.length >= 3 && (
                    <div className={`email-check ${emailValid ? 'valid' : 'invalid'}`}>
                      {emailValid ? 'Format valide' : 'Adresse email invalide'}
                    </div>
                  )}
                </div>

                <div className="form-row">
                  <div className="form-group form-group-half">
                    <label htmlFor="reg-prenom">Prénom *</label>
                    <input
                      type="text"
                      id="reg-prenom"
                      value={prenom}
                      onChange={(e) => setPrenom(e.target.value)}
                      placeholder="Prénom"
                      required
                    />
                  </div>
                  <div className="form-group form-group-half">
                    <label htmlFor="reg-nom">Nom *</label>
                    <input
                      type="text"
                      id="reg-nom"
                      value={nom}
                      onChange={(e) => setNom(e.target.value)}
                      placeholder="Nom"
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="reg-password">Mot de passe *</label>
                  <input
                    type="password"
                    id="reg-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Min. 8 car., majuscule, minuscule, chiffre"
                    minLength={8}
                    autoComplete="new-password"
                    required
                  />
                  {password.length > 0 && (
                    <div className="password-strength">
                      <div className="password-strength-bar">
                        <div
                          className="password-strength-fill"
                          data-level={pwStrength.level}
                        />
                      </div>
                      <div className="password-strength-text" data-level={pwStrength.level}>
                        {pwStrength.label}
                        {!pwAcceptable && password.length > 0 && (
                          <> — {password.length < 8 ? '8 caractères min.' : 'majuscule, minuscule et chiffre requis'}</>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="reg-password-confirm">Confirmer *</label>
                  <input
                    type="password"
                    id="reg-password-confirm"
                    value={passwordConfirm}
                    onChange={(e) => setPasswordConfirm(e.target.value)}
                    placeholder="Retapez votre mot de passe"
                    autoComplete="new-password"
                    required
                  />
                  {!passwordsMatch && (
                    <div className="pseudo-check taken">Les mots de passe ne correspondent pas</div>
                  )}
                </div>

                <button type="submit" className="login-button" disabled={!canSubmit}>
                  {loading ? 'Création...' : 'Créer mon compte'}
                </button>

                <button
                  type="button"
                  className="login-form-back"
                  onClick={() => { setShowForm(false); setError(''); }}
                >
                  Retour aux autres options
                </button>
              </form>
            </div>
          )}

          <div className="login-links">
            <span>Déjà un compte ?</span>
            <Link to="/">Se connecter</Link>
          </div>

          <AuthLegalFooter />
        </div>
      </div>
    </div>
  );
}

function PseudoIcon() {
  return (
    <svg className="oauth-icon" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

function OAuthIcon({ provider }: { provider: string }) {
  switch (provider) {
    case 'google':
      return (
        <svg className="oauth-icon" viewBox="0 0 24 24" width="20" height="20">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
        </svg>
      );
    case 'facebook':
      return (
        <svg className="oauth-icon" viewBox="0 0 24 24" width="20" height="20">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" fill="#1877F2"/>
        </svg>
      );
    default:
      return null;
  }
}
